import React, { useState, useEffect, useRef, useCallback } from 'react';
import MessageItem from './MessageItem';
import MessageComposer from './MessageComposer';

function csrfToken() {
  const el = document.querySelector('meta[name="csrf-token"]');
  return el ? el.content : '';
}

// @context TGW-1769 スレッド右パネル（狭幅では全画面オーバーレイ）。返信はスレッド内のみ
//   （「チャンネルにも送信」は要件で廃止 = also_send_to_channel は送らない）。
//   WS の返信イベントは親(SlackCircleChat)経由で registerIncoming に流れてくる
//   （チャンネル購読は MessagePane 側の1本に集約し、二重購読を避けるため）。
export default function ThreadPanel({
  circleId,
  channel,
  rootMessage,
  currentUser,
  canManage,
  registerIncoming,
  onClose,
}) {
  const [root, setRoot] = useState(rootMessage);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const listRef = useRef(null);
  // @context TGW-1769 送信後1.5秒のフォールバック再取得がパネルを閉じた後に走ると
  //   不要な通信とアンマウント後の setState になるため、マウント状態で握りつぶす（Copilot指摘）。
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  // パネルを開いたままチャンネル側でルートが削除された場合など、親からの
  // rootMessage 更新（is_deleted 化）を内部 state に反映する
  useEffect(() => {
    setRoot(rootMessage);
  }, [rootMessage]);

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/message/thread?root_message_id=${rootMessage.id}`,
          {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          },
        );
        if (res.ok && !aborted) {
          const data = await res.json();
          setRoot(data.root || rootMessage);
          setReplies(data.replies || []);
        } else if (!aborted) {
          setLoadError(true);
        }
      } catch (e) {
        if (!aborted) setLoadError(true);
      } finally {
        // 失敗時も「読み込み中…」で固まらないよう必ず解除する（Copilot指摘）
        if (!aborted) setLoading(false);
      }
    })();
    return () => {
      aborted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootMessage.id]);

  // WSで届いた返信（自分の送信の反映もこれで行う）
  useEffect(() => {
    registerIncoming((reply) => {
      if (reply.thread_root_message_id !== rootMessage.id) return;
      setReplies((prev) =>
        prev.some((r) => r.id === reply.id) ? prev : [...prev, reply],
      );
    });
    return () => registerIncoming(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootMessage.id]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [replies.length, loading]);

  const handleSend = useCallback(
    async ({ body, file, mentionUserIds, mentionChannel, mentionEveryone }) => {
      const formData = new FormData();
      formData.append('root_message_id', rootMessage.id);
      if (body) formData.append('body', body);
      if (file) formData.append('file', file);
      (mentionUserIds || []).forEach((id) =>
        formData.append('mention_user_ids[]', id),
      );
      if (mentionChannel) formData.append('mention_channel', 'true');
      if (mentionEveryone) formData.append('mention_everyone', 'true');

      await fetch('/api/message/thread_reply', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken() },
        credentials: 'same-origin',
        body: formData,
      });
      // 反映は WS(message_created) 経由。WS断のフォールバックとして1.5秒後に未達なら再取得
      setTimeout(async () => {
        if (!mountedRef.current) return;
        const res = await fetch(
          `/api/message/thread?root_message_id=${rootMessage.id}`,
          {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          },
        );
        if (res.ok && mountedRef.current) {
          const data = await res.json();
          setReplies(data.replies || []);
        }
      }, 1500);
    },
    [rootMessage.id],
  );

  const noop = () => {};

  return (
    <div className="slackch-thread-inner">
      <div className="slackch-thread-header">
        <b>スレッド</b>
        <span className="slackch-thread-header-channel">
          {channel.isPrivate ? '🔒' : '#'} {channel.name}
        </span>
        <button
          type="button"
          className="slackch-thread-close"
          onClick={onClose}
          aria-label="閉じる"
        >
          ✕
        </button>
      </div>
      <div className="slackch-thread-list" ref={listRef}>
        <MessageItem
          message={root}
          currentUser={currentUser}
          canManage={canManage}
          isGrouped={false}
          threadsEnabled={false}
          onOpenThread={noop}
          onToggleReaction={noop}
          onDelete={noop}
          onRetry={noop}
        />
        <div className="slackch-thread-count">{replies.length}件の返信</div>
        {loading && <div className="slackch-pane-loading">読み込み中…</div>}
        {loadError && (
          <div className="slackch-pane-error">
            スレッドを読み込めませんでした
          </div>
        )}
        {replies.map((reply) => (
          <MessageItem
            key={reply.id}
            message={reply}
            currentUser={currentUser}
            canManage={canManage}
            isGrouped={false}
            threadsEnabled={false}
            onOpenThread={noop}
            onToggleReaction={noop}
            onDelete={noop}
            onRetry={noop}
          />
        ))}
      </div>
      {/* 削除済み親のスレッドは閲覧のみ（BEも thread_reply を404にするため入力欄を出さない） */}
      {root.is_deleted ? (
        <div className="slackch-thread-count">
          削除されたメッセージのスレッドには返信できません
        </div>
      ) : (
        <MessageComposer
          circleId={circleId}
          channel={channel}
          onSend={handleSend}
          placeholder="返信する…"
        />
      )}
    </div>
  );
}
